'use client';

import React, { useEffect, useState } from 'react';
import {
    DefineIcon,
    DeployIcon,
    DevelopmentIcon,
    EmpathizeIcon,
    IdeateIcon,
    TestIcon
} from './StepIcons';
import { StepItem } from './Stepper';

const DesignThinkingProcess: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Define steps with their titles, subtitles, and icons
    const steps: StepItem[] = [
        {
            title: 'Empathize',
            subtitle: 'Understanding user needs',
            icon: <EmpathizeIcon />,
            status: 'completed'
        },
        {
            title: 'Define',
            subtitle: 'Framing the problem',
            icon: <DefineIcon />,
            status: 'completed'
        },
        {
            title: 'Ideate',
            subtitle: 'Generating solutions',
            icon: <IdeateIcon />,
            status: 'completed'
        },
        {
            title: 'Development',
            subtitle: 'Building the app',
            icon: <DevelopmentIcon />,
            status: 'completed'
        },
        {
            title: 'Test',
            subtitle: 'Validating solutions',
            icon: <TestIcon />,
            status: 'completed'
        },
        {
            title: 'Launch',
            subtitle: 'Launching the product',
            icon: <DeployIcon />,
            status: 'completed'
        }
    ];

    // Auto-advance through steps with 1-second delay
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prevStep) => (prevStep + 1) % steps.length);
        }, 1000);

        return () => clearInterval(interval);
    }, [steps.length]);

    return (
        <div className="relative">
            {/* Just the stepper component without borders */}
            <div className="bg-transparent py-6">
                <div className="w-full py-8 px-4 sm:px-6">
                    {/* Desktop view - horizontal */}
                    <div className="hidden md:flex md:relative">
                        {steps.map((step, index) => (
                            <React.Fragment key={`step-${index}`}>
                                <div className={`flex-1 flex flex-col items-center ${index === steps.length - 1 ? 'flex-1' : ''}`}>
                                    <div className="w-full flex flex-col items-center">
                                        <div
                                            className={`
                        w-12 h-12 rounded-full bg-white border-3 ${index <= activeStep ? 'border-[#FFD0C6]' : 'border-[#DDE6F3]'}
                        flex items-center justify-center relative
                        shadow-lg ${index <= activeStep ? 'shadow-[#FFD0C6]/20' : 'shadow-[#DDE6F3]/30'} mb-3
                        transition-all duration-300 ease-in-out
                        ${index === activeStep ? 'scale-110' : ''}
                        group
                      `}
                                        >
                                            {/* Animated glow effect for active step */}
                                            {index === activeStep && (
                                                <div className="absolute inset-0 rounded-full bg-[#FFA28B]/10 animate-pulse"></div>
                                            )}

                                            <div className={`${index <= activeStep ? 'text-gray-800' : 'text-gray-400'} transition-colors duration-300`}>
                                                {step.icon}
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <h3 className={`text-sm font-bold uppercase ${index > activeStep ? 'text-gray-400' : 'text-gray-800'}`}>
                                                {step.title}
                                            </h3>
                                            <p className={`text-xs ${index > activeStep ? 'text-gray-300' : 'text-gray-500'} mt-1`}>
                                                {step.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress line between steps */}
                                {index < steps.length - 1 && (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className={`h-1 w-full ${index < activeStep ? 'bg-[#FFA28B]' : 'bg-[#DDE6F3]'} relative rounded-full overflow-hidden`}>
                                            {/* Add animated gradient effect for active step */}
                                            {index === activeStep && (
                                                <div className="absolute inset-0 bg-gradient-to-r from-[#FFA28B]/0 via-white/30 to-[#FFA28B]/0 animate-shimmer"></div>
                                            )}

                                            {/* Add rounded corners at the joints */}
                                            <div className="absolute left-0 top-0 bottom-0 w-2 rounded-l-full bg-inherit"></div>
                                            <div className="absolute right-0 top-0 bottom-0 w-2 rounded-r-full bg-inherit"></div>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Mobile view - vertical */}
                    <div className="md:hidden">
                        {steps.map((step, index) => (
                            <React.Fragment key={`step-mobile-${index}`}>
                                <div className="flex items-start">
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`
                        w-12 h-12 rounded-full bg-white border-3 ${index <= activeStep ? 'border-[#FFD0C6]' : 'border-[#DDE6F3]'}
                        flex items-center justify-center relative
                        shadow-lg ${index <= activeStep ? 'shadow-[#FFD0C6]/20' : 'shadow-[#DDE6F3]/30'} mb-3
                        transition-all duration-300 ease-in-out
                        ${index === activeStep ? 'scale-110' : ''}
                        group
                      `}
                                        >
                                            {/* Animated glow effect for active step */}
                                            {index === activeStep && (
                                                <div className="absolute inset-0 rounded-full bg-[#FFA28B]/10 animate-pulse"></div>
                                            )}

                                            <div className={`${index <= activeStep ? 'text-gray-800' : 'text-gray-400'} transition-colors duration-300`}>
                                                {step.icon}
                                            </div>
                                        </div>

                                        {/* Vertical progress line */}
                                        {index < steps.length - 1 && (
                                            <div className="flex flex-col items-center">
                                                <div className={`w-1 h-12 ${index < activeStep ? 'bg-[#FFA28B]' : 'bg-[#DDE6F3]'} relative rounded-full overflow-hidden`}>
                                                    {/* Add animated gradient effect for active step */}
                                                    {index === activeStep && (
                                                        <div className="absolute inset-0 bg-gradient-to-b from-[#FFA28B]/0 via-white/30 to-[#FFA28B]/0 animate-shimmer-vertical"></div>
                                                    )}

                                                    {/* Add rounded corners at the joints */}
                                                    <div className="absolute left-0 right-0 top-0 h-2 rounded-t-full bg-inherit"></div>
                                                    <div className="absolute left-0 right-0 bottom-0 h-2 rounded-b-full bg-inherit"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="ml-4 pb-8">
                                        <h3 className={`text-sm font-bold uppercase ${index > activeStep ? 'text-gray-400' : 'text-gray-800'}`}>
                                            {step.title}
                                        </h3>
                                        <p className={`text-xs ${index > activeStep ? 'text-gray-300' : 'text-gray-500'} mt-1`}>
                                            {step.subtitle}
                                        </p>
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DesignThinkingProcess; 